import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { KenBurns } from "../components/KenBurns";
import { useEffect } from "react";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: {
      delay: 0.3 + i * 0.14,
      duration: 1.15,
      ease: [0.65, 0, 0.15, 1],
    },
  }),
};

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.85, ease: [0.7, 0, 0.2, 1] },
  }),
};

const ConciergeHero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A0A0B] text-[#F1EEE8]"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <KenBurns
          duration={7500}
          images={[
            "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?auto=format&fit=crop&w=2400&q=85",
            "https://images.unsplash.com/photo-1583778176476-4a8b02a64c01?auto=format&fit=crop&w=2400&q=85",
            "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=2400&q=85",
            "https://images.unsplash.com/photo-1560523159-4a9692d222f8?auto=format&fit=crop&w=2400&q=85",
          ]}
        />
      </motion.div>
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#0A0A0B]/60 via-[#0A0A0B]/40 to-[#0A0A0B]" />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(10,10,11,0.9) 100%)",
        }}
      />

      <motion.div
        style={{ y: textY }}
        className="relative z-30 max-w-[1600px] mx-auto px-6 md:px-10 pt-40 md:pt-52 pb-16 min-h-[100svh] flex flex-col justify-between"
      >
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex items-center gap-4 text-[#232326]/80 text-[0.7rem] tracking-[0.28em] uppercase mb-10"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 hover:text-[#8A6F45] transition-colors"
              data-testid="concierge-back-home"
            >
              <ArrowLeft size={14} weight="light" />
              {t.concierge.backToHome}
            </Link>
            <span className="w-4 h-px bg-[#F1EEE8]/40" />
            <span className="text-[#8A6F45]">{t.concierge.overline}</span>
          </motion.div>

          <h1 className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.8rem,8.5vw,10rem)]">
            {[t.concierge.hero1, t.concierge.hero2, t.concierge.hero3].map((line, i) => (
              <span key={i} className="block">
                <span className="reveal-mask">
                  <motion.span
                    className="inline-block"
                    variants={lineVariants}
                    custom={i}
                    initial="hidden"
                    animate="visible"
                  >
                    {i === 2 ? (
                      <span className="italic-serif text-[#8A6F45]">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              </span>
            ))}
          </h1>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="max-w-2xl text-[#232326]/78 text-lg leading-relaxed"
        >
          {t.concierge.lead}
        </motion.p>
      </motion.div>
    </section>
  );
};

const ServicesGrid = () => {
  const { t } = useLang();
  return (
    <section className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-40" data-testid="concierge-services">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {t.concierge.services.map((s, i) => (
            <motion.div
              key={s.k}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={revealUp}
              custom={i}
              className="group"
              data-testid={`concierge-service-${s.k}`}
            >
              <div className="relative overflow-hidden clip-card aspect-[4/5] mb-6 bg-[#E6E3DC]">
                <img
                  src={`${s.img}?auto=format&fit=crop&w=1200&q=80`}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 text-[0.66rem] tracking-[0.28em] uppercase text-[#F1EEE8] border border-[#F1EEE8]/40 px-3 py-1 bg-[#0A0A0B]/40 backdrop-blur-sm">
                  {s.k}
                </div>
              </div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight mb-3 group-hover:text-[#8A6F45] transition-colors">
                {s.title}
                <span className="text-[#8A6F45]">.</span>
              </h3>
              <p className="text-[#232326]/70 leading-relaxed text-[0.98rem]">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ConciergeCTA = () => {
  const { t } = useLang();
  return (
    <section className="relative bg-[#0A0A0B] text-[#F1EEE8] py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-40">
        <KenBurns
          duration={6500}
          images={[
            "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=2000&q=80",
            "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=2000&q=80",
            "https://images.unsplash.com/photo-1713279495561-fd27d8417bcc?auto=format&fit=crop&w=2000&q=80",
          ]}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B] via-[#0A0A0B]/85 to-[#0A0A0B]" />

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.7, 0, 0.2, 1] }}
          className="font-display font-medium leading-[1.05] tracking-tight text-[clamp(2.6rem,7vw,7rem)] whitespace-pre-line mb-10"
        >
          {t.concierge.ctaTitle}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ delay: 0.2, duration: 0.9 }}
          className="text-[#232326]/75 max-w-2xl mx-auto leading-relaxed text-lg mb-12"
        >
          {t.concierge.ctaBody}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ delay: 0.35, duration: 0.9 }}
        >
          <Link to="/#contact" className="btn-primary-inverse" data-testid="concierge-cta-contact">
            {t.concierge.ctaButton}
            <ArrowUpRight size={14} weight="light" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default function Concierge() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <main className="bg-[#F1EEE8] text-[#232326] min-h-screen" data-testid="concierge-page">
      <Nav />
      <ConciergeHero />
      <ServicesGrid />
      <ConciergeCTA />
      <Footer />
    </main>
  );
}
